import type { MusicTrack } from './types';
import {
  DEFAULT_GAME_GAIN_DB,
  DEFAULT_MUSIC_GAIN_DB,
  GAIN_DB_MAX,
  GAIN_DB_MIN,
} from './types';
import bundledManifest from './manifest.json';

function asTrack(row: unknown): MusicTrack | null {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return null;
  const rec = row as Record<string, unknown>;
  const id = String(rec.id || '').trim();
  if (!id) return null;
  const previewRaw = rec.preview_url != null ? String(rec.preview_url).trim() : '';
  return {
    id,
    display_name: String(rec.display_name || id),
    file: String(rec.file || `${id}.m4a`),
    has_vocals: Boolean(rec.has_vocals),
    bpm: typeof rec.bpm === 'number' ? rec.bpm : null,
    energy: rec.energy != null ? String(rec.energy) : null,
    tone: rec.tone != null ? String(rec.tone) : null,
    duration_s: typeof rec.duration_s === 'number' ? rec.duration_s : null,
    license: rec.license != null ? String(rec.license) : null,
    preview_url: previewRaw || null,
  };
}

/**
 * Accept both a bare track array and `{ tracks: [...] }`.
 */
export function loadTracks(raw: unknown): MusicTrack[] {
  let rows: unknown[] = [];
  if (Array.isArray(raw)) {
    rows = raw;
  } else if (raw && typeof raw === 'object' && Array.isArray((raw as { tracks?: unknown }).tracks)) {
    rows = (raw as { tracks: unknown[] }).tracks;
  }
  const out: MusicTrack[] = [];
  for (const row of rows) {
    const track = asTrack(row);
    if (track) out.push(track);
  }
  return out;
}

/** Bundled catalog (flat array on main). */
export function loadBundledTracks(): MusicTrack[] {
  return loadTracks(bundledManifest);
}

/** Map slider percent (0–100) → dB (−40…0). 100% = 0 dB, 60% ≈ −16 dB. */
export function percentToGainDb(pct: number): number {
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  const span = GAIN_DB_MAX - GAIN_DB_MIN;
  return GAIN_DB_MIN + (clamped / 100) * span;
}

/** Map dB (−40…0) → slider percent (0–100). */
export function gainDbToPercent(db: number): number {
  const clamped = Math.max(GAIN_DB_MIN, Math.min(GAIN_DB_MAX, Number(db) || 0));
  const span = GAIN_DB_MAX - GAIN_DB_MIN;
  if (span <= 0) return 100;
  return ((clamped - GAIN_DB_MIN) / span) * 100;
}

export function defaultGameGainDb(): number {
  return DEFAULT_GAME_GAIN_DB;
}

export function defaultMusicGainDb(): number {
  return DEFAULT_MUSIC_GAIN_DB;
}

export { gainDbToLinearVolume } from './previewAudio';
